import {GenericError, WebGLError} from "@buildwithflux/core";
import Link from "@material-ui/core/Link/Link";
import Typography from "@material-ui/core/Typography/Typography";
import {ErrorBoundary, Scope, withSentryRouting} from "@sentry/react";
import {useCallback} from "react";
import {Route, RouteProps} from "react-router-dom";

import {FluxLogger} from "modules/storage_engine/connectors/LogConnector";
import R from "resources/Namespace";
import ErrorPage from "components/pages/errors/common/ErrorPage";

// FIX: Move SentryRoute outside the component to prevent remounting children on every render
// When withSentryRouting(Route) is called inside the component, it creates a new component type
// on every render, causing React to unmount and remount the entire child tree
const SentryRoute = withSentryRouting(Route);

// TODO: move this out of here to shared resources or something
export const defaultErrorMessage =
    "We're sorry, an internal error has occurred. A batch of cybernetically enhanced racoons have been dispatched to address the issue. Please refresh and try again.";

function fallbackComponent(props: {error: Error}) {
    // ESLint erroneously complains that these aren't consts. We're not using useMemo for them,
    // so that doesn't apply.
    //
    // eslint-disable-next-line react-memo/require-usememo
    let message = defaultErrorMessage;
    // eslint-disable-next-line react-memo/require-usememo
    let title = "500 Error";
    let content = <></>;
    // Handle specific known errors. Note: error message text can change
    // differ between library versions and across browsers, so for errors
    // where you have a more robust method of determining error type,
    // try to avoid text matching. However, in JavaScript that's not always
    // feasible.

    // Handle flux-specific webgl error, or an error that mentions webgl
    if (props.error instanceof WebGLError || props.error?.message.match(/webgl/gi)) {
        title = "Uh oh... some graphics features are missing";
        message = "";
        content = (
            <Typography>
                We can't open this project because WebGL 2 isn't supported or is disabled in your browser.
                <br />
                <br />
                If your browser supports WebGL 2, check out{" "}
                <Link href={R.externalUrls.documentation.setup_browser}>this help article</Link> to find out how to
                enable it.
            </Typography>
        );
    }

    return (
        <ErrorPage title={title} pageTitle={"Uh oh... App crashed"} subtitle={message} statusCode={500}>
            {content}
        </ErrorPage>
    );
}

export function RouteWithErrorBoundary(props: RouteProps) {
    const onBeforeCapture = useCallback(
        (scope: Scope) => {
            scope.setTag("errorBoundary", true);

            if (typeof props.path === "string") {
                scope.setTag("location", props.path);
            } else if (Array.isArray(props.path)) {
                scope.setTag("location", props.path.join(", "));
            } else {
                FluxLogger.captureError(
                    new GenericError("Route path is not a string but type:" + typeof props.path, {path: props.path}),
                );
            }
        },
        [props.path],
    );
    return (
        <ErrorBoundary fallback={fallbackComponent} beforeCapture={onBeforeCapture}>
            <SentryRoute {...props} />
        </ErrorBoundary>
    );
}
